import {Injectable, NgZone} from '@angular/core';
import {environment} from '../../environments/environment';

/**
 * Client for atlas-agent-service.
 *
 * Deliberately uses `fetch` rather than HttpClient or EventSource:
 *  - the chat endpoint is a POST that streams SSE back, and EventSource can only
 *    issue a GET with no headers, so it cannot carry the operator's token;
 *  - HttpClient buffers the whole response, which defeats streaming.
 *
 * The token is the operator's own, read from the same place the rest of the app
 * reads it. There is no service account behind the agent: everything it does
 * upstream is done as the person typing.
 */

/** A tool's own rows, re-shaped by the service. Never derived here. */
export interface ResultTable {
  columns: string[];
  rows: string[][];
  /** Rows the query matched, which may exceed the rows carried in `rows`. */
  rowCount: number;
}

/** A guide hit, for the context cards. */
export interface ResultDoc {
  id: string;
  title: string;
  area: string;
  snippet: string;
  rank: number;
}

/**
 * A file the assistant produced during a tool call.
 *
 * Downloaded through the service rather than rebuilt in the browser: the bytes
 * the operator opens have to be the same bytes cpi_upload_excel would send, or
 * reviewing the file before importing it proves nothing.
 */
export interface ResultFile {
  id: string;
  name: string;
  sizeBytes: number;
}

/**
 * A picture a tool rendered, shown inline in the transcript.
 *
 * The panel gets an id and fetches the bytes itself, because the route wants the
 * operator's header and an `<img src>` cannot send one. `width` and `height` are
 * the preview's own, used to hold the space before it loads.
 */
export interface ResultImage {
  id: string;
  name: string;
  mime: string;
  width: number;
  height: number;
  sizeBytes: number;
}

/**
 * One field of a pending write. `from` is present only where the service could
 * establish the current value — absent means unknown, never empty.
 */
export interface ChangeField {
  field: string;
  from?: string;
  to: string;
}

/** One question inside an `ask` card. */
export interface AskQuestion {
  id: string;
  text: string;
  options: string[];
  multiple: boolean;
  allowCustom: boolean;
}

/**
 * Something the assistant has written down for future sessions. See the
 * service's notes.ts — these are recollections it will re-read months from now,
 * which is why the panel has to make them visible and deletable.
 */
export interface AgentNote {
  id: string;
  scope: string;
  text: string;
  createdAt: string;
}

export interface AgentEvent {
  type: string;
  // session
  sessionId?: string;
  provider?: string;
  model?: string;
  // text_delta / reasoning_delta
  text?: string;
  // tool_start / tool_result / approval_* / ask
  callId?: string;
  tool?: string;
  kind?: 'read' | 'write';
  args?: any;
  ok?: boolean;
  summary?: string;
  table?: ResultTable;
  docs?: ResultDoc[];
  file?: ResultFile;
  images?: ResultImage[];
  /** Human-readable rendering of a write's arguments, for the approval card. */
  preview?: string;
  changes?: ChangeField[];
  /**
   * Auto mode: the approval card is a record of a write that has already gone
   * ahead, not a question. Rendering it with approve/decline buttons would
   * offer a choice that no longer exists.
   */
  auto?: boolean;
  approved?: boolean;
  // ask
  title?: string;
  questions?: AskQuestion[];
  // followups
  items?: string[];
  // ungrounded — figures in the answer that no tool result contains
  figures?: string[];
  // text_trim — the exact suffix to remove from the answer already streamed
  suffix?: string;
  // note_saved
  id?: string;
  scope?: string;
  // done / error
  stop?: string;
  turns?: number;
  message?: string;
  raw?: any;
}

export interface AgentHealth {
  ok: boolean;
  tools: number;
  writeTools: number;
  defaultProvider: string;
  providers: {[name: string]: boolean};
}

/**
 * One model a provider can reach. An object, not a string: Gemini reports a
 * display name alongside the id and the picker shows both, and the raw ids
 * ("google/gemma-3-27b-it:free") are not what an operator recognises.
 */
export interface AgentModel {
  name: string;
  displayName?: string;
}

export interface ModelList {
  provider: string;
  /** What the service is configured to use, when it reports one. */
  configured?: string;
  models: AgentModel[];
}

/**
 * A file the operator attached, as the service reports it back.
 *
 * The parse result comes from the server rather than being re-derived here: the
 * chip says "3 sayfa · 4.812 satır" because the server actually opened the
 * workbook, and a client-side guess from the extension would be a claim the
 * panel cannot back.
 */
export interface AgentFile {
  id: string;
  name: string;
  kind: 'sheet' | 'image' | 'text' | 'json';
  sizeBytes: number;
  width: number;
  height: number;
  tables: {name: string; columns: string[]; rowCount: number}[];
  /** Non-empty when the server could not read the file. */
  problem: string;
  /** Echoed so the first upload of a conversation can start the session. */
  sessionId: string;
}

/**
 * How one AI client is told to reach the MCP server, as that operator.
 *
 * `snippet` carries the operator's real token and is only ever handed to the
 * clipboard; `masked` is the copy that goes on screen. Same text otherwise, so
 * what they read is what they paste.
 */
export interface McpConnection {
  /** The server's Streamable HTTP endpoint. */
  url: string;
  /**
   * The Authorization header value, verbatim.
   *
   * It already begins with "Bearer " — the value CMS issues carries the scheme,
   * and a client that adds one of its own sends "Bearer Bearer" and gets a 401
   * from the gateway with nothing in the message that says why.
   */
  token: string;
  /** What stands in for the token until the operator asks to see it. */
  masked: string;
}

export interface ChatHandlers {
  onEvent: (event: AgentEvent) => void;
  onDone: () => void;
  onError: (message: string) => void;
}

export interface ChatStream {
  abort: () => void;
}

@Injectable({providedIn: 'root'})
export class AgentService {
  /**
   * Carries the /agent prefix, the way every other endpoint in the environment
   * file carries its own path. The call sites below append only the route.
   */
  private readonly base: string = (environment as any).agentEndpoint || 'http://localhost:8790/agent';

  constructor(private zone: NgZone) {}

  health(): Promise<AgentHealth> {
    return fetch(this.base + '/health').then(r => r.json());
  }

  /**
   * Which models this deployment's keys can actually reach. Not every provider
   * can list — Claude has no listing endpoint here — so a rejection is a normal
   * answer, not an error to surface.
   */
  models(provider: string): Promise<ModelList> {
    return fetch(this.base + '/models?provider=' + encodeURIComponent(provider)).then(r => {
      if (!r.ok) {
        return {provider: provider, models: []} as ModelList;
      }
      return r.json();
    });
  }

  approve(sessionId: string, callId: string, approved: boolean): Promise<void> {
    return fetch(this.base + '/approve', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({sessionId: sessionId, callId: callId, approved: approved}),
    }).then(() => undefined);
  }

  /** Answers to an `ask` card, keyed by question id. */
  respond(sessionId: string, callId: string, answers: {[id: string]: string[]}): Promise<void> {
    return fetch(this.base + '/respond', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({sessionId: sessionId, callId: callId, answers: answers}),
    }).then(() => undefined);
  }

  /**
   * What the assistant is carrying into future sessions. Operator-scoped by the
   * same headers every other call uses, so this can only ever return the
   * caller's own notes.
   */
  notes(): Promise<AgentNote[]> {
    return fetch(this.base + '/notes', {headers: this.operatorHeaders()})
      .then(r => (r.ok ? r.json() : {notes: []}))
      .then(body => body.notes || []);
  }

  forget(id: string): Promise<boolean> {
    return fetch(this.base + '/notes/' + encodeURIComponent(id), {
      method: 'DELETE',
      headers: this.operatorHeaders(),
    }).then(r => r.ok);
  }

  /**
   * Where the operator's own AI client should point, and what it must send, so
   * that it reaches the same tools this panel uses — as them.
   *
   * The token is the one already in this browser. The panel does not mint a
   * second session, which matters because CMS allows one per operator: a client
   * configured from here shares the session the operator is sitting in, and
   * dies with it.
   *
   * This used to return four ready-made registrations — a `claude mcp add`
   * line, a Desktop mcpServers block, a Codex config.toml stanza, and a plain
   * pair. Four snippets is four things to keep true against four clients that
   * change their config format on their own schedule, and the dialog they
   * filled was a scroll of near-identical code blocks. The endpoint and the
   * header are the whole of what a client needs; how a given one is told about
   * them is that client's documentation to keep current, not ours.
   *
   * What was learned building those, kept because it is not obvious and will be
   * asked again: Claude Desktop speaks stdio to a local process and will not
   * send an Authorization header to a remote server, so it needs `mcp-remote`
   * as a bridge; Codex selects the HTTP transport by `url` (not `command`) and
   * carries the token in `http_headers`, not `bearer_token_env_var`, which
   * would prepend a second "Bearer". Neither Claude's web chat nor its mobile
   * app can register a local server at all: their connectors want a public
   * HTTPS address and their own sign-in.
   */
  mcpConnection(): McpConnection {
    return {
      url: (environment as any).mcpEndpoint || 'http://127.0.0.1:8791/mcp',
      token: localStorage.getItem('token') || '',
      /**
       * Deliberately NOT a shortened token.
       *
       * The first version showed `Bearer eyJhbGc…E2r3pw` — the head, an
       * ellipsis, the tail — which reads as a token and was duly pasted into a
       * config file. It failed three layers down, inside the client's HTTP
       * library, with "Cannot convert argument to a ByteString ... value of
       * 8230": the ellipsis. Nothing in that message says "you copied the wrong
       * thing".
       *
       * So the placeholder must be impossible to mistake for a credential. The
       * real value is one press away, and "Göster" reveals it for anyone who
       * would rather select the text by hand.
       */
      masked: '«KOPYALA DÜĞMESİNE BASIN»',
    };
  }

  /**
   * `json` is false for the multipart upload: setting Content-Type there would
   * override the boundary the browser generated and the server would parse
   * nothing.
   */
  private operatorHeaders(json = true): {[k: string]: string} {
    const headers: {[k: string]: string} = json
      ? {'Content-Type': 'application/json'}
      : {};
    const token = localStorage.getItem('token');
    // Verbatim, like the app's own HTTP interceptor. The value CMS issues
    // already begins with "Bearer ", so adding a prefix here would send
    // "Bearer Bearer" and removing it returns 401 from the gateway.
    if (token) {
      headers['Authorization'] = token;
    }
    const userId = localStorage.getItem('userID');
    if (userId) {
      headers['x-operator-id'] = userId;
    }
    const username = localStorage.getItem('username');
    if (username) {
      headers['x-operator-name'] = username;
    }
    return headers;
  }

  /**
   * Upload one file and get back what the server made of it.
   *
   * Multipart, and no session id required: the service opens the conversation
   * on the first upload and returns its id, because attaching a spreadsheet and
   * then typing the question about it is the ordinary order of events.
   */
  /**
   * Fetch a file the assistant produced and hand it to the browser to save.
   *
   * A plain <a href> cannot be used: the route needs the operator's
   * Authorization header, which a navigation does not carry. So the bytes come
   * back through fetch and are handed over as an object URL, revoked once the
   * click has been dispatched.
   */
  downloadFile(fileId: string, name: string): Promise<void> {
    return fetch(this.base + '/files/' + encodeURIComponent(fileId), {
      headers: this.operatorHeaders(false),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Dosya indirilemedi (HTTP ' + response.status + ').');
        }
        return response.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        link.click();
        URL.revokeObjectURL(url);
      });
  }

  /**
   * Fetch a picture a tool rendered, as a URL an <img> can use.
   *
   * The same route and the same reason as downloadFile: the bytes need the
   * operator's Authorization header, and an image element sends none. What comes
   * back is an object URL the caller owns and must revoke — see the component's
   * ngOnDestroy, which is where a panel scrolled through fifty poster previews
   * gets its memory back.
   */
  loadImage(fileId: string): Promise<string> {
    return fetch(this.base + '/files/' + encodeURIComponent(fileId), {
      headers: this.operatorHeaders(false),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Görsel yüklenemedi (HTTP ' + response.status + ').');
        }
        return response.blob();
      })
      .then(blob => URL.createObjectURL(blob));
  }

  uploadFile(
    file: File,
    sessionId: string,
    provider: string,
    model?: string,
  ): Promise<AgentFile> {
    const form = new FormData();
    form.append('file', file, file.name);

    const query = ['provider=' + encodeURIComponent(provider)];
    if (sessionId) {
      query.push('sessionId=' + encodeURIComponent(sessionId));
    }
    if (model) {
      query.push('model=' + encodeURIComponent(model));
    }

    // No Content-Type header: the browser has to set it, because only it knows
    // the multipart boundary it generated.
    return fetch(this.base + '/files?' + query.join('&'), {
      method: 'POST',
      headers: this.operatorHeaders(false),
      body: form,
    }).then(response =>
      response.json().then((body: any) => {
        if (!response.ok) {
          throw new Error(body && body.error ? body.error : 'HTTP ' + response.status);
        }
        return body as AgentFile;
      }),
    );
  }

  chat(
    message: string,
    provider: string,
    sessionId: string,
    handlers: ChatHandlers,
    model?: string,
    fileIds?: string[],
    approvalMode?: string,
  ): ChatStream {
    const controller = new AbortController();

    const body: any = {message: message, provider: provider};
    if (sessionId) {
      body.sessionId = sessionId;
    }
    // Only read when a session is created — the service holds one provider
    // instance per session, so switching models means a new session.
    if (model) {
      body.model = model;
    }
    // Only the files attached to THIS message. Earlier ones stay reachable to
    // the assistant through its own file tools.
    if (fileIds && fileIds.length) {
      body.fileIds = fileIds;
    }
    // Sent on every turn, never remembered by the service: a mode the operator
    // can see in the prompt bar and a mode the server is applying must be the
    // same thing, and only the request can guarantee that.
    if (approvalMode) {
      body.approvalMode = approvalMode;
    }

    fetch(this.base + '/chat', {
      method: 'POST',
      headers: this.operatorHeaders(),
      body: JSON.stringify(body),
      signal: controller.signal,
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error('HTTP ' + response.status + ': ' + text);
          });
        }
        // TS 3.5's DOM lib predates the ReadableStream reader types, and the
        // es2017 lib has no async iterator, so this is a manual pump rather
        // than `for await`.
        const reader = (response.body as any).getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const pump = (): Promise<void> =>
          reader.read().then((chunk: any) => {
            if (chunk.done) {
              this.zone.run(() => handlers.onDone());
              return;
            }
            buffer += decoder.decode(chunk.value, {stream: true});
            const frames = buffer.split('\n\n');
            buffer = frames.pop();

            for (let i = 0; i < frames.length; i++) {
              const line = frames[i].split('\n').filter(l => l.indexOf('data: ') === 0)[0];
              if (!line) {
                continue;
              }
              let event: AgentEvent;
              try {
                event = JSON.parse(line.substring(6));
              } catch (e) {
                continue;
              }
              // Zone.js does not patch the stream reader, so without this the
              // view would not refresh until some unrelated event ticked it.
              this.zone.run(() => handlers.onEvent(event));
            }
            return pump();
          });

        return pump();
      })
      .catch(err => {
        if (err && err.name === 'AbortError') {
          this.zone.run(() => handlers.onDone());
          return;
        }
        this.zone.run(() => handlers.onError(err && err.message ? err.message : String(err)));
      });

    return {abort: () => controller.abort()};
  }
}
